import { type ReactNode } from "react";
import { useLocation, Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Rocket } from "lucide-react";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";

/**
 * Forced subscription landing when the active subscription has lapsed.
 * Mirrors the legacy `App.tsx` subscription-expiry enforcement: users whose
 * subscription is `expired` / `unsubscribed` (or an ended trial) are not allowed
 * to keep using the app unless they use sponsored access (roles 5, 6 and 7)
 * or are on the customers / subscription-settings paths.
 */
export function SubscriptionStatusGuard({ children }: { children: ReactNode }) {
  const { allowed, pending } = useSubscriptionAccess();
  const { pathname } = useLocation();

  const blocked =
    !allowed &&
    !pathname.includes("customers") &&
    !pathname.includes("/settings/subscription");

  if (blocked && pending) {
    return (
      <div
        className="space-y-6 p-6"
        role="status"
        aria-label="Loading page"
        aria-busy="true"
      >
        <div aria-hidden="true" className="space-y-6 motion-safe:animate-pulse">
          <div className="h-8 w-48 rounded-lg bg-muted" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((key) => (
              <div key={key} className="h-28 rounded-xl bg-muted" />
            ))}
          </div>
          <div className="h-64 rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  if (!blocked) return <>{children}</>;

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4">
      <Card variant="outlined" sx={{ maxWidth: 480, width: "100%" }}>
        <CardContent sx={{ padding: "2rem", textAlign: "center" }}>
          <Rocket className="mx-auto mb-4 h-10 w-10 text-primary" />
          <h2 className="text-xl font-bold tracking-tight">
            Your subscription has ended
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Your PlanetWise subscription is no longer active. Subscribe now to
            continue using the platform.
          </p>
          <div className="mt-6 flex flex-col items-center gap-2">
            <Button
              variant="contained"
              onClick={() => window.open("/plans", "_blank")}
            >
              View Plans
            </Button>
            <Button component={Link} to="/settings/subscription" variant="text">
              Manage subscription
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
